<template>
  <SoDialog
    v-model="value"
    card-title="Review changes"
    :max-width="560"
    data-testid="winner-review-dialog"
  >
    <SoAlert
      type="warning"
      title="You are about to save the following changes:"
    />
    <SoSimpleTable
      :headers="['Winner', 'Field', 'From', 'To']"
      :items="diffRows"
      class="mt-4 mb-2 border rounded"
      data-testid="winner-review-table"
    />
    <SoAlert
      v-if="isComplete"
      type="success"
      title="Changes saved"
      class="mt-4"
      data-testid="winner-review-success"
    />
    <SoAlert
      v-if="hasError"
      type="error"
      title="Some changes could not be saved"
      class="mt-4"
      data-testid="winner-review-error"
    />
    <div class="d-flex justify-end mt-6">
      <template v-if="isComplete">
        <SoButton
          title="Close"
          large
          data-testid="winner-review-close-btn"
          @click="close"
        />
      </template>
      <template v-else>
        <SoButton
          title="Cancel"
          class="mr-2"
          large
          data-testid="winner-review-cancel-btn"
          @click="close"
        />
        <SoButton
          title="Save"
          type="success"
          large
          :loading="isSaving"
          :disabled="isSaving || !changes.length"
          data-testid="winner-review-save-btn"
          @click="save"
        />
      </template>
    </div>
  </SoDialog>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoSimpleTable from "@/components/SoSimpleTable.vue"
  import { useSnackbarStore } from "@/stores/snackbar"
  import { LoyaltyMutator } from "@/mutators/loyalty/LoyaltyMutator"
  import { IWinnerChange } from "@/models/operations/draw/drawWinner.types"
  import { yesNo } from "@/helpers/stringHelpers"

  export default defineComponent({
    components: { SoDialog, SoAlert, SoButton, SoSimpleTable },
    props: {
      modelValue: {
        type: Boolean,
        default: false,
      },
      drawId: {
        type: String,
        required: true,
      },
      changes: {
        type: Array as PropType<IWinnerChange[]>,
        default: () => [],
      },
    },
    emits: ["update:modelValue", "saved"],
    data() {
      return {
        snackbar: useSnackbarStore(),
        updateMutation: LoyaltyMutator.updateWinner(),
        isSaving: false,
        isComplete: false,
        hasError: false,
        // Whether a save was attempted this session — drives whether closing
        // needs to trigger a parent refetch.
        attempted: false,
      }
    },
    computed: {
      value: {
        get(): boolean {
          return this.modelValue
        },
        set(newValue: boolean) {
          this.$emit("update:modelValue", newValue)
        },
      },
      diffRows(): string[][] {
        return this.changes.flatMap((change) => {
          const rows: string[][] = []
          if (change.countyChanged) {
            rows.push([
              change.surname,
              "County",
              change.fromCounty || "—",
              change.toCounty || "—",
            ])
          }
          if (change.anonChanged) {
            rows.push([
              change.surname,
              "Anonymous",
              yesNo(change.fromAnon),
              yesNo(change.toAnon),
            ])
          }
          return rows
        })
      },
    },
    watch: {
      modelValue(open: boolean) {
        if (open) {
          this.isSaving = false
          this.isComplete = false
          this.hasError = false
          this.attempted = false
        }
      },
    },
    methods: {
      async save() {
        if (!this.changes.length) return
        this.attempted = true
        this.isSaving = true
        this.hasError = false
        // One UpdateWinner call per changed winner (single-winner endpoint),
        // sending only the changed fields. allSettled so one failure doesn't
        // abort the rest — a REST failure rejects and is tallied below.
        const results = await Promise.allSettled(
          this.changes.map((change) =>
            this.updateMutation.mutateAsync({
              drawId: this.drawId,
              winnerId: change.id,
              county: change.countyChanged ? change.toCounty : undefined,
              nameVisibilityOptOut: change.anonChanged
                ? change.toAnon
                : undefined,
            }),
          ),
        )
        this.isSaving = false
        const failed = results.filter(
          (result) => result.status === "rejected",
        ).length
        if (failed > 0) {
          this.hasError = true
          this.snackbar.addError(
            `Failed to save ${failed} of ${this.changes.length} change${
              this.changes.length === 1 ? "" : "s"
            }`,
          )
          return
        }
        this.isComplete = true
        this.snackbar.addSuccess(
          `Saved ${this.changes.length} winner update${
            this.changes.length === 1 ? "" : "s"
          }`,
        )
      },
      close() {
        // Reconcile the table with the server after any save attempt (covers
        // partial successes too); a plain cancel changed nothing.
        if (this.attempted) this.$emit("saved")
        this.value = false
      },
    },
  })
</script>
